import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { User, Heart, Send, MapPin, Clock, HandHelping, ChevronLeft, MessageSquare, CheckCircle2, XCircle, AlertTriangle, Megaphone } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* People — the phone's contacts for Living Citizens. Everyone you've met in the city,
   sorted by closeness. Tap a friend to text them (they reply in character), see where
   they work and when, and help them with personal favors when they're close enough
   to ask. Favors pay out real cash and real friendship. */

const LEVEL_COLORS = ["text-zinc-500", "text-sky-300", "text-teal-300", "text-emerald-300", "text-amber-300"];

const iso = (minAgo) => new Date(Date.now() - minAgo * 60000).toISOString();
const DEMO_FRIENDS = [
  { ref: "CTZ-DEMO01", name: "Emeka Abernathy", occupation: "teacher", suburb: "North Market", workplace: "school",
    schedule: { shift_start: 8, shift_end: 16 }, affinity: 24, level: 3, level_name: "friend", last_met: iso(300), favor_available: true },
  { ref: "CTZ-DEMO02", name: "Donna Marsh", occupation: "bartender", suburb: "The Docks", workplace: "bar",
    schedule: { shift_start: 16, shift_end: 2 }, affinity: 9, level: 2, level_name: "familiar", last_met: iso(1400), favor_available: false, grudge: true },
  { ref: "CTZ-DEMO03", name: "Gus Grady", occupation: "bus driver", suburb: "Millrow", workplace: "depot",
    schedule: { shift_start: 5, shift_end: 15 }, affinity: 2, level: 1, level_name: "acquaintance", last_met: iso(2800), favor_available: false },
];
const DEMO_TEXTS = [
  { from: "them", text: "good to hear from you! long day at school", at: iso(50) },
  { from: "me", text: "Anything I can help with?", at: iso(49) },
  { from: "them", text: "actually… maybe. ask me next time you see me", at: iso(48) },
];

export default function PeopleApp({ demo, serverId, token, accent, sfx }) {
  const [friends, setFriends] = useState(demo ? DEMO_FRIENDS : null);
  const [fame, setFame] = useState(demo ? { fame: 30, infamy: 4, fame_title: "a local hero", infamy_title: "whispered about", gossip: [{ text: "Gossip Gal helped half of North Market", good: true }] } : null);
  const [open, setOpen] = useState(null);          // friend object
  const [texts, setTexts] = useState([]);
  const [draft, setDraft] = useState("");
  const [favors, setFavors] = useState([]);
  const [busy, setBusy] = useState("");
  const scrollRef = useRef(null);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/life/friends`, { params: { token } })
      .then((r) => setFriends(r.data.friends)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/nc/servers/${serverId}/life/favors`, { params: { token } })
      .then((r) => setFavors(r.data.favors.filter((f) => f.status === "active"))).catch(() => {});
    api.get(`/nc/servers/${serverId}/life/fame`, { params: { token } })
      .then((r) => setFame(r.data)).catch(() => {});
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const loadTexts = useCallback((ref) => {
    if (demo) { setTexts(DEMO_TEXTS); return; }
    api.get(`/nc/servers/${serverId}/life/texts`, { params: { token, ref } })
      .then((r) => setTexts(r.data.messages)).catch(() => {});
  }, [demo, serverId, token]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999 }); }, [texts]);

  const openFriend = (f) => { sfx?.keyTick(); setOpen(f); if (demo || f.phone_shared) loadTexts(f.ref); else setTexts([]); };

  const sendText = async () => {
    if (!demo && !open?.phone_shared) return toast.info("Swap numbers in person first — they'll text you after that");
    if (!draft.trim() || busy) return;
    sfx?.keyTick();
    if (demo) {
      setTexts((t) => [...t, { from: "me", text: draft, at: new Date().toISOString() },
                             { from: "them", text: "ha! tell me in person next time 💛", at: new Date().toISOString() }]);
      setDraft("");
      return;
    }
    setBusy("text");
    const line = draft.trim();
    setDraft("");
    setTexts((t) => [...t, { from: "me", text: line, at: new Date().toISOString() }]);
    try {
      const r = await api.post(`/nc/servers/${serverId}/life/text`, { ref: open.ref, token, text: line });
      setTexts((t) => [...t, { from: "them", text: r.data.reply, at: r.data.at }]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  const askFavor = async () => {
    if (busy || demo) { if (demo) toast.success("Emeka tells you about the rent… (preview)"); return; }
    setBusy("favor");
    try {
      const r = await api.post(`/nc/servers/${serverId}/life/favor/request`, { ref: open.ref, token });
      toast.success(r.data.message);
      load();
      setOpen(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  const completeFavor = async (f, choice = "") => {
    if (busy || demo) return;
    setBusy(f.id);
    try {
      const r = await api.post(`/nc/servers/${serverId}/life/favor/${f.id}/complete`, { token, choice });
      toast.success(r.data.message + (r.data.reward ? ` +$${r.data.reward}` : ""));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  const makeAmends = async (mode) => {
    if (busy || !open) return;
    if (demo) { toast.success("Apology accepted — Donna softens. (preview)"); return; }
    setBusy(`amends-${mode}`);
    try {
      const r = await api.post(`/nc/servers/${serverId}/life/apologize`, { ref: open.ref, token, mode });
      toast.success(r.data.message);
      setOpen((o) => ({ ...o, grudge: false, affinity: r.data.affinity }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  /* ---- detail view ---- */
  if (open) {
    const lvl = LEVEL_COLORS[open.level] || LEVEL_COLORS[0];
    return (
      <div data-testid="people-detail" className="flex flex-col gap-3 p-4 h-full">
        <button data-testid="people-back" onClick={() => setOpen(null)} className="self-start flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors">
          <ChevronLeft className="w-4 h-4" /> People
        </button>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center border" style={{ borderColor: `${accent}60`, background: `${accent}18` }}>
            <User className="w-5 h-5" style={{ color: accent }} />
          </div>
          <div>
            <p className="font-sans font-semibold text-sm text-white">{open.name}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-white/50">{open.occupation} · {open.suburb}</p>
          </div>
          <span className={`ml-auto font-mono text-[9px] uppercase tracking-[0.15em] flex items-center gap-1 ${lvl}`}>
            <Heart className="w-3 h-3" /> {open.level_name}
          </span>
        </div>
        <p className="font-mono text-[9px] text-white/50 flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> works the {open.workplace} {open.schedule?.shift_start ?? "?"}:00–{open.schedule?.shift_end ?? "?"}:00 ·
          <MapPin className="w-3 h-3 ml-1" /> lives {open.suburb}
        </p>
        {open.favor_available && (
          <button data-testid="people-favor-ask" onClick={askFavor} disabled={!!busy}
                  className="font-mono text-[10px] uppercase tracking-[0.15em] border px-3 py-2.5 transition-colors flex items-center gap-2 disabled:opacity-40"
                  style={{ borderColor: `${accent}70`, color: accent }}>
            <HandHelping className="w-3.5 h-3.5" /> They seem troubled — ask what's wrong
          </button>
        )}
        {open.grudge && (
          <div data-testid="people-grudge" className="border border-red-400/30 bg-red-400/5 rounded-xl p-3 flex flex-col gap-2">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-red-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> They're still upset about what they saw
            </p>
            <div className="flex gap-2">
              <button data-testid="people-apologize-words" onClick={() => makeAmends("words")} disabled={!!busy}
                      className="flex-1 font-mono text-[9px] uppercase tracking-[0.15em] border border-white/20 hover:border-white/50 text-white/80 rounded-lg px-3 py-2 transition-colors disabled:opacity-40">
                Apologise
              </button>
              <button data-testid="people-apologize-gift" onClick={() => makeAmends("gift")} disabled={!!busy}
                      className="flex-1 font-mono text-[9px] uppercase tracking-[0.15em] border rounded-lg px-3 py-2 transition-colors disabled:opacity-40"
                      style={{ borderColor: `${accent}70`, color: accent }}>
                Make it right — $120
              </button>
            </div>
          </div>
        )}
        <div ref={scrollRef} data-testid="people-texts" className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 border-t border-white/10 pt-3">
          {texts.length === 0 && <p className="font-mono text-[10px] text-white/40 text-center py-6">{open.phone_shared ? "No texts yet — say hi." : "You don't have their number yet — swap numbers in person."}</p>}
          {texts.map((m, i) => (
            <p key={i} className={m.from === "me"
              ? "self-end max-w-[80%] font-mono text-[11px] text-white bg-white/10 border border-white/10 rounded-2xl rounded-br-sm px-3 py-2"
              : "self-start max-w-[80%] font-mono text-[11px] text-white/90 rounded-2xl rounded-bl-sm px-3 py-2 border"}
                 style={m.from === "them" ? { background: `${accent}22`, borderColor: `${accent}45` } : {}}>
              {m.text}
            </p>
          ))}
          {busy === "text" && <p className="font-mono text-[9px] text-white/40 animate-pulse">typing…</p>}
        </div>
        <div className="flex gap-2">
          <input data-testid="people-text-input" value={draft} onChange={(e) => setDraft(e.target.value)} disabled={!demo && !open.phone_shared}
                 onKeyDown={(e) => e.key === "Enter" && sendText()}
                 placeholder={open.phone_shared ? `Text ${open.name.split(" ")[0]}…` : "Swap numbers in person first"} maxLength={240}
                 className="flex-1 bg-white/5 border border-white/15 focus:border-white/40 rounded-full px-4 py-2.5 font-mono text-[11px] text-white focus:outline-none" />
          <button data-testid="people-text-send" onClick={sendText} disabled={!!busy || !draft.trim() || (!demo && !open.phone_shared)}
                  className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40 transition-opacity"
                  style={{ background: accent }}>
            <Send className="w-4 h-4 text-black" />
          </button>
        </div>
      </div>
    );
  }

  /* ---- list view ---- */
  return (
    <div data-testid="people-app" className="flex flex-col gap-3 p-4">
      {favors.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="font-mono text-[9px] tracking-[0.25em] uppercase" style={{ color: accent }}>Favors in progress</p>
          {favors.map((f) => (
            <div key={f.id} data-testid={`favor-${f.id}`} className="border border-white/15 bg-white/5 p-3 rounded-xl">
              <p className="font-mono text-[10px] text-white/90 leading-relaxed">{f.brief}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-white/50">{f.citizen_name} · {f.type}{f.reward ? ` · pays $${f.reward}` : ""}</span>
                {f.type === "advice" ? (
                  <div className="flex flex-col gap-1.5 w-full mt-1">
                    {(f.spec?.choices || []).map((c, i) => (
                      <button key={i} data-testid={`favor-choice-${i}`} onClick={() => completeFavor(f, c)} disabled={!!busy}
                              className="text-left font-mono text-[10px] text-white/80 border border-white/15 hover:border-white/40 rounded-lg px-3 py-2 transition-colors disabled:opacity-40">
                        {c}
                      </button>
                    ))}
                  </div>
                ) : (
                  <button data-testid={`favor-complete-${f.id}`} onClick={() => completeFavor(f)} disabled={!!busy}
                          className="ml-auto font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-40 flex items-center gap-1.5"
                          style={{ borderColor: `${accent}70`, color: accent }}>
                    <CheckCircle2 className="w-3 h-3" /> Done
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/50">People you know ({(friends || []).length})</p>
      {fame && (fame.fame_title || fame.infamy_title) && (
        <div data-testid="people-fame" className="border border-white/10 bg-white/5 rounded-xl px-3 py-2.5">
          <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/45 flex items-center gap-1.5 mb-1">
            <Megaphone className="w-3 h-3" /> The street says
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            {fame.fame_title && <p className="font-mono text-[10px] text-emerald-300">“{fame.fame_title}”</p>}
            {fame.infamy_title && <p className="font-mono text-[10px] text-red-400">“{fame.infamy_title}”</p>}
          </div>
          {fame.gossip?.length > 0 && (
            <p className="font-mono text-[9px] text-white/50 mt-1 italic">Latest: {fame.gossip[fame.gossip.length - 1].text}</p>
          )}
        </div>
      )}
      {!friends && <p className="font-mono text-[10px] text-white/40">Loading…</p>}
      {friends && friends.length === 0 && (
        <p data-testid="people-empty" className="font-mono text-[10px] text-white/50 leading-relaxed py-6 text-center">
          Nobody yet. Walk the city, stop someone, introduce yourself — the city remembers.
        </p>
      )}
      {(friends || []).map((f) => (
        <button key={f.ref} data-testid={`people-friend-${f.ref}`} onClick={() => openFriend(f)}
                className="flex items-center gap-3 border border-white/10 hover:border-white/30 bg-white/5 rounded-xl px-3 py-2.5 transition-colors text-left">
          <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-white/70" />
          </div>
          <div className="min-w-0">
            <p className="font-sans font-semibold text-xs text-white truncate">{f.name}</p>
            <p className="font-mono text-[8px] uppercase tracking-[0.15em] text-white/45">{f.occupation} · {f.suburb}</p>
          </div>
          {f.favor_available && <HandHelping data-testid={`people-favor-flag-${f.ref}`} className="w-3.5 h-3.5 shrink-0 animate-pulse" style={{ color: accent }} />}
          <span className={`ml-auto font-mono text-[8px] uppercase tracking-[0.12em] shrink-0 ${LEVEL_COLORS[f.level] || LEVEL_COLORS[0]}`}>{f.level_name}</span>
        </button>
      ))}
    </div>
  );
}
